/**
 * GuideSearch — top-bar fuzzy search across every section's searchIndex.
 * Uses fuse.js (~10 kB gz) for tolerance to typos and partial matches.
 * Results jump straight to the matching section (and anchor when one is
 * declared on the search entry).
 */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  Box, InputAdornment, TextField, Paper, List, ListItemButton, ListItemText, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Fuse from 'fuse.js';
import { SECTIONS } from './sections';

interface FlatEntry {
  sectionId: string;
  sectionLabel: string;
  heading: string;
  anchor?: string;
  body: string;
}

const buildIndex = (): FlatEntry[] => {
  const out: FlatEntry[] = [];
  for (const s of SECTIONS) {
    for (const e of (s.searchIndex || [])) {
      out.push({
        sectionId: s.id,
        sectionLabel: s.label,
        heading: e.heading,
        anchor: e.anchor,
        body: e.body,
      });
    }
    // Always have a "section landing" entry so plain section names search.
    if (!s.searchIndex || s.searchIndex.length === 0) {
      out.push({ sectionId: s.id, sectionLabel: s.label, heading: s.label, body: s.label });
    }
  }
  return out;
};

interface Props {
  onPick: (sectionId: string, anchor?: string) => void;
}

const GuideSearch: React.FC<Props> = ({ onPick }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const fuse = useMemo(() => new Fuse(buildIndex(), {
    keys: [
      { name: 'heading',      weight: 0.6 },
      { name: 'sectionLabel', weight: 0.3 },
      { name: 'body',         weight: 0.1 },
    ],
    threshold: 0.4,
    ignoreLocation: true,
    minMatchCharLength: 2,
  }), []);

  const results = useMemo(() => {
    if (!q.trim()) return [];
    return fuse.search(q.trim(), { limit: 12 }).map(r => r.item);
  }, [q, fuse]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative', maxWidth: 460, width: '100%' }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search the guide…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
      />
      {open && q.trim() && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 1100,
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          {results.length === 0 ? (
            <Typography sx={{ p: 1.5, fontSize: '0.85rem', color: 'text.secondary' }}>
              No matches.
            </Typography>
          ) : (
            <List dense disablePadding>
              {results.map((r, i) => (
                <ListItemButton
                  key={`${r.sectionId}:${r.anchor || ''}:${i}`}
                  onClick={() => { onPick(r.sectionId, r.anchor); setOpen(false); setQ(''); }}
                >
                  <ListItemText
                    primary={r.heading}
                    secondary={r.sectionLabel}
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: '0.72rem' }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default GuideSearch;
